// src/pages/admin/products/Units.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  Package,
  Ruler,
  RefreshCw,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react'
import UnitsService, { typeOptions } from '../../../services/unitsService'
import AddUnitModal from '../../../components/modals/AddUnitModal'
import ViewUnitModal from '../../../components/modals/ViewUnitModal'
import EditUnitModal from '../../../components/modals/EditUnitModal'
import DeleteUnitModal from '../../../components/modals/DeleteUnitModal'
import PageLoader from '../../../components/ui/PageLoader'
import CardLoader from '../../../components/ui/CardLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const Units = () => {
  // State management
  const [units, setUnits] = useState([])
  const [filteredUnits, setFilteredUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [actionMenuOpen, setActionMenuOpen] = useState(null)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [toast, setToast] = useState(null)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Fetch units on component mount
  useEffect(() => {
    fetchUnits()
  }, [])

  // Filter and search units whenever dependencies change
  useEffect(() => {
    let filtered = [...units]

    // Apply search filter
    if (searchTerm) {
      filtered = UnitsService.searchUnits(filtered, searchTerm)
    }

    // Apply type filter
    if (typeFilter) {
      filtered = UnitsService.filterByType(filtered, typeFilter)
    }

    // Apply status filter
    if (statusFilter !== '') {
      const isActive = statusFilter === 'active'
      filtered = UnitsService.filterByStatus(filtered, isActive)
    }

    setFilteredUnits(filtered)
  }, [units, searchTerm, typeFilter, statusFilter])

  // Fetch units from API
  const fetchUnits = async () => {
    try {
      setLoading(true)
      console.log('📏 Fetching units...')
      const response = await UnitsService.getAllUnits()
      
      if (response.success) {
        console.log('✅ Units fetched successfully:', response.data)
        setUnits(response.data.units || response.data || [])
      } else {
        console.log('⚠️ Failed to fetch units:', response.message)
        setUnits([])
      }
    } catch (error) {
      console.error('❌ Error fetching units:', error)
      setUnits([])
    } finally {
      setLoading(false)
    }
  }

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  // Handle type filter change
  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value)
  }

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setTypeFilter('')
    setStatusFilter('')
  }

  // Handle action menu toggle
  const handleActionMenuToggle = (unitId) => {
    setActionMenuOpen(actionMenuOpen === unitId ? null : unitId)
  }

  // Close action menu
  const closeActionMenu = () => {
    setActionMenuOpen(null)
  }

  // Handle view unit
  const handleViewUnit = (unit) => {
    setSelectedUnit(unit)
    setShowViewModal(true)
    closeActionMenu()
  }

  // Handle edit unit
  const handleEditUnit = (unit) => {
    setSelectedUnit(unit)
    setShowEditModal(true)
    closeActionMenu()
  }

  // Handle delete unit
  const handleDeleteUnit = (unit) => {
    setSelectedUnit(unit)
    setShowDeleteModal(true)
    closeActionMenu()
  }

  // Handle status toggle
  const handleStatusToggle = async (unit) => {
    const unitId = unit._id
    const newStatus = !unit.isActive

    try {
      setActionLoading(prev => ({ ...prev, [unitId]: true }))
      
      const response = await UnitsService.toggleUnitStatus(unitId, newStatus)
      
      if (response.success) {
        console.log('✅ Unit status toggled successfully')
        setUnits(prev => prev.map(u => 
          u._id === unitId ? { ...u, isActive: newStatus } : u
        ))
        showToast(`Unit ${newStatus ? 'activated' : 'deactivated'} successfully`)
      } else {
        console.error('⚠️ Failed to toggle unit status:', response.message)
        showToast('Failed to update unit status', 'error')
      }
    } catch (error) {
      console.error('❌ Error toggling unit status:', error)
      showToast('Failed to update unit status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [unitId]: false }))
    }
    
    closeActionMenu()
  }

  // Handle unit added
  const handleUnitAdded = (newUnit) => {
    console.log('✅ Unit added:', newUnit)
    setUnits(prev => [newUnit, ...prev])
    showToast('Unit created successfully')
  }

  // Handle unit updated
  const handleUnitUpdated = (updatedUnit) => {
    console.log('✅ Unit updated:', updatedUnit)
    setUnits(prev => prev.map(unit => 
      unit._id === updatedUnit._id ? updatedUnit : unit
    ))
    showToast('Unit updated successfully')
  }

  // Handle unit deleted
  const handleUnitDeleted = (deletedUnitId) => {
    console.log('✅ Unit deleted:', deletedUnitId)
    setUnits(prev => prev.filter(unit => unit._id !== deletedUnitId))
    showToast('Unit deleted successfully')
  }

  // Get units summary
  const unitsSummary = UnitsService.getUnitsSummary(units)

  // Loading state
  if (loading && units.length === 0) {
    return (
      <PageLoader 
        headerTitle="Units of Measurement"
        title="Loading units..."
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Units of Measurement</h1>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={fetchUnits}
              disabled={loading}
              leftIcon={RefreshCw}
              variant="outline"
              className="bg-white border-gray-300"
            >
              Refresh
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              leftIcon={Plus}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Add Unit
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Units</p>
                  <p className="text-2xl font-bold text-gray-900">{unitsSummary.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{unitsSummary.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <XCircle className="h-8 w-8 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Inactive</p>
                  <p className="text-2xl font-bold text-gray-900">{unitsSummary.inactive}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Ruler className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Types</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.keys(unitsSummary.byType).filter(type => unitsSummary.byType[type] > 0).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Units</h2>
              </div>
              {(searchTerm || typeFilter || statusFilter) && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search by name or mobile..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 h-11 border-gray-300"
                />
              </div>

              {/* Type Filter */}
              <div>
                <select
                  value={typeFilter}
                  onChange={handleTypeFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Types</option>
                  {typeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-600">
              Showing {filteredUnits.length} of {units.length} units
            </div>
          </CardContent>
        </Card>


          <Card className="bg-white">
            <CardContent className="p-0">
              {loading ? (
                <CardLoader title="Loading units..." py="py-12" />
              ) : filteredUnits.length === 0 ? (
                <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {units.length === 0 ? 'No units found' : 'No units match your filters'}
            </h3>
            <p className="text-gray-600 mb-4">
              {units.length === 0 
                ? 'Get started by creating your first unit'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {units.length === 0 && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Unit
              </Button>
            )}
                </div>
              ) : (
                <>
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abbreviation
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                    </th>
                  </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUnits.map((unit) => (
                    <tr key={unit._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                <span className="text-2xl mr-3">
                  {UnitsService.getTypeIcon(unit.type)}
                </span>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                  {unit.name}
                  </div>
                  {unit.description && (
                  <div className="text-sm text-gray-500 truncate max-w-xs">
                    {unit.description}
                  </div>
                  )}
                </div>
                </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-medium text-gray-900">
                {unit.abbreviation}
                </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                ${UnitsService.getTypeColor(unit.type) === 'blue' ? 'bg-blue-100 text-blue-800' :
                  UnitsService.getTypeColor(unit.type) === 'green' ? 'bg-green-100 text-green-800' :
                  UnitsService.getTypeColor(unit.type) === 'purple' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'}`}
                >
                {UnitsService.getTypeDisplayLabel(unit.type)}
                </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  unit.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
                }`}>
                {unit.isActive ? 'Active' : 'Inactive'}
                </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex items-center justify-center space-x-1">
                <button
                  onClick={() => handleViewUnit(unit)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="View unit"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleEditUnit(unit)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Edit unit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteUnit(unit)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Delete unit"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                </div>
                    </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Mobile Cards */}
                <div className="lg:hidden space-y-4 p-4">
                  {filteredUnits.map((unit) => (
                    <div key={unit._id} className="border rounded-lg p-4 bg-white">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="text-2xl mr-2">
                              {UnitsService.getTypeIcon(unit.type)}
                            </span>
                            <h3 className="font-medium text-gray-900">{unit.name}</h3>
                          </div>
                          {unit.description && (
                            <p className="text-sm text-gray-500 mb-2">{unit.description}</p>
                          )}
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">
                              <strong>Abbr:</strong> {unit.abbreviation}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                              ${UnitsService.getTypeColor(unit.type) === 'blue' ? 'bg-blue-100 text-blue-800' :
                                UnitsService.getTypeColor(unit.type) === 'green' ? 'bg-green-100 text-green-800' :
                                UnitsService.getTypeColor(unit.type) === 'purple' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'}`}
                            >
                              {UnitsService.getTypeDisplayLabel(unit.type)}
                            </span>
                          </div>
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => handleActionMenuToggle(unit._id)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                          
                          {actionMenuOpen === unit._id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border">
                              <div className="py-1">
                                <button
                                  onClick={() => handleViewUnit(unit)}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </button>
                                <button
                                  onClick={() => handleEditUnit(unit)}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Unit
                                </button>
                                <button
                                  onClick={() => handleDeleteUnit(unit)}
                                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Unit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => handleStatusToggle(unit)}
                          disabled={actionLoading[unit._id]}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            unit.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          } ${actionLoading[unit._id] ? 'opacity-50' : 'hover:opacity-80'}`}
                        >
                          {actionLoading[unit._id] && (
                            <ButtonSpinner />
                          )}
                          {unit.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <AddUnitModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onUnitAdded={handleUnitAdded}
        />

        <ViewUnitModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          unit={selectedUnit}
        />

        <EditUnitModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUnitUpdated={handleUnitUpdated}
          unit={selectedUnit}
        />

        <DeleteUnitModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onUnitDeleted={handleUnitDeleted}
          unit={selectedUnit}
        />
      </div>
    </div>
  )
}

export default Units